import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "./ui/Loader";
import Modal from "./ui/Modal";
import "../App.css";
import { swalError, swalSuccess } from "../utils/swalAlert";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import PaymentModal from "./Payment";
import TurnstileWidget from "./ui/TurnstileWidget";

const serverUrl = process.env.REACT_APP_SERVER_URL;
const turnstileSiteKey =
  process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY ||
  process.env.CLOUDFLARE_TURNSTILE_SITE_KEY ||
  "0x4AAAAAAEgNn41IKXm7hap5";
document.title = "GradeMe AI - AI Assistant For Teachers";

const api = {
  get: async (endpoint) => {
    const response = await fetch(`${serverUrl}/api${endpoint}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
    if (!response.ok) throw await response.json();
    return { data: await response.json(), status: response.status };
  },
  post: async (endpoint, data) => {
    const response = await fetch(`${serverUrl}/api${endpoint}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    });

    const resData = await response.json();

    if (!response.ok) throw resData;
    return { data: resData, status: response.status };
  },
};


// Compute once when the file loads
const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPadOS 13+

const isAndroid = /Android/i.test(navigator.userAgent);

const isMobileDevice =
  isAndroid ||
  isIOS ||
  /webOS|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent);

const isInStandaloneMode =
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true; // iOS Safari flag; matchMedia alone isn't reliable pre-iOS 13


export default function Login() {
  const { setIsAuthenticated, setUser, setPrimaryRole } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [loginTab, setLoginTab] = useState("phone"); // 'phone' or 'email'
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phoneNumber: "",
  });
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [phoneDialCode, setPhoneDialCode] = useState("");
  const [forgotPasswordPhoneDialCode, setForgotPasswordPhoneDialCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordTab, setForgotPasswordTab] = useState("email");
  const [forgotPasswordPhone, setForgotPasswordPhone] = useState("");
  const [deferredPrompt, setDeferredPrompt] = useState(
    () => window.deferredInstallPrompt || null
  );
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [multipleAccounts, setMultipleAccounts] = useState(null);
  const [isAccountSelectionModalOpen, setIsAccountSelectionModalOpen] =
    useState(false);
  const [paymentToken, setPaymentToken] = useState(null);
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  // helper to strip plus, dashes, spaces and parentheses
  const cleanPhone = (val) =>
    val ? String(val).replace(/[+\-\s()]/g, "") : "";

const validateStrictPhoneNumber = (phoneNumber, dialCode) => {
  if (!phoneNumber) return { isValid: false, message: "Phone number is required." };
  
  const phoneDigits = phoneNumber.replace(/\D/g, "");
  
  // 1. Check basic E.164 length boundaries
  if (phoneDigits.length < 7 || phoneDigits.length > 15) {
    return { isValid: false, message: "Phone number must be between 7 and 15 digits." };
  }
  
  // 2. Specific checks for major regions
  if (dialCode === "91") {
    // India: 91 + 10 digits = 12 digits
    if (phoneDigits.length !== 12) {
      return { isValid: false, message: "Please enter a valid 10-digit Indian phone number." };
    }
    const nationalNumber = phoneDigits.slice(2);
    if (!/^[6-9]/.test(nationalNumber)) {
      return { isValid: false, message: "Indian mobile numbers must start with 6, 7, 8, or 9." };
    }
  } else if (dialCode === "1") {
    // US/Canada: 1 + 10 digits = 11 digits
    if (phoneDigits.length !== 11) {
      return { isValid: false, message: "Please enter a valid 10-digit US/Canada phone number." };
    }
    const nationalNumber = phoneDigits.slice(1);
    if (/^[01]/.test(nationalNumber)) {
      return { isValid: false, message: "Please enter a valid US/Canada area code." };
    }
  } else {
    // Other countries: ensure national part has at least 4 digits
    const nationalNumber = dialCode ? phoneDigits.slice(dialCode.length) : phoneDigits;
    if (nationalNumber.length < 4) {
      return { isValid: false, message: "Please enter a complete phone number." };
    }
  }
  
  return { isValid: true };
};

  const [showManualInstructions, setShowManualInstructions] = useState(false);

  useEffect(() => {
    // Only show install prompt on mobile devices when not in standalone mode
    if (!isMobileDevice || isInStandaloneMode) return;

    // Show prompt for mobile users
    setShowInstallPrompt(true);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredInstallPrompt = e;
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    const handlePWAInstallable = () => {
      if (window.deferredInstallPrompt) {
        setDeferredPrompt(window.deferredInstallPrompt);
        setShowInstallPrompt(true);
      }
    };

    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      window.deferredInstallPrompt = null;
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("pwa-installable", handlePWAInstallable);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("pwa-installable", handlePWAInstallable);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === "accepted") {
          setShowInstallPrompt(false);
          setDeferredPrompt(null);
          window.deferredInstallPrompt = null;
        }
      } catch (err) {
        console.error("Error triggering PWA install prompt:", err);
        setShowManualInstructions(true);
      }
    } else {
      setShowManualInstructions(true);
    }
  };

  const redirectBasedOnRole = useCallback(
    (role, category) => {
      if (category === "INDIVIDUAL") {
        navigate("/individual", { replace: true });
        return;
      } else if (category === "UPSC") {
        navigate("/upsc", { replace: true });
        return;
      }
      const routes = {
        superAdmin: "/superadmin",
        admin: "/admin",
        instructor: "/instructor",
        student: "/student",
      };
      navigate(routes[role] || "/login", { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthCode = params.get("code");
    if (!oauthCode) return;

    const checkSignedIn = async () => {
      try {
        const userResponse = await api.get("/user/signed-in");
        const userData = userResponse.data;
        if (userResponse.status === 200 && userData && userData._id) {
          setUser(userData);
          setIsAuthenticated(true);
          const firstRole = userData.role?.[0] || null;
          setPrimaryRole(firstRole);
          if (firstRole) {
            localStorage.setItem("primaryRole", firstRole);
          }
          redirectBasedOnRole(firstRole, userData.category);
        }
      } catch (error) {
        // Not signed in. Do nothing.
      }
    };

    checkSignedIn();
  }, [
    location.search,
    redirectBasedOnRole,
    setUser,
    setIsAuthenticated,
    setPrimaryRole,
  ]);

  const handleLoginSuccess = async () => {
    const userResponse = await api.get("/user/signed-in");
    const userData = userResponse.data;
    if (userResponse.status === 200 && userData && userData._id) {
      setUser(userData);
      setIsAuthenticated(true);

      const availableRoles = userData.role || [];
      const firstRole = availableRoles[0] || null;

      setPrimaryRole(firstRole);
      if (firstRole) {
        localStorage.setItem("primaryRole", firstRole);
      }
      redirectBasedOnRole(firstRole, userData.category);
      setFormData({ email: "", password: "", phoneNumber: "" });
      setTurnstileToken("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (turnstileSiteKey && !turnstileToken) {
        swalError("Please complete the Cloudflare Turnstile verification before logging in.");
        setLoading(false);
        return;
      }

      const payload = {
        password: formData.password,
        turnstileToken,
      };

      if (loginTab === "email") {
        payload.email = formData.email;
      } else {
        const phoneValidation = validateStrictPhoneNumber(formData.phoneNumber, phoneDialCode);
        if (!phoneValidation.isValid) {
          swalError(phoneValidation.message);
          setLoading(false);
          return;
        }
        payload.phoneNumber = cleanPhone(formData.phoneNumber);
      }

      const response = await api.post("/auth/signin", payload);

      if (response.data.success) {
        await handleLoginSuccess();
      } else if (response.data.expired && response.data.paymentToken) {
        setPaymentToken(response.data.paymentToken);
        setShowPaymentPrompt(true);
      } else if (response.data.multipleAccounts) {
        // If multiple accounts found, show selection modal
        setMultipleAccounts(response.data.students);
        setIsAccountSelectionModalOpen(true);
      }
    } catch (error) {
      turnstileRef.current?.reset();
      setTurnstileToken("");
      if (error.expired && error.paymentToken) {
        setPaymentToken(error.paymentToken);
        setShowPaymentPrompt(true);
      } else if (error.multipleAccounts) {
        setMultipleAccounts(error.students);
        setIsAccountSelectionModalOpen(true);
      } else {
        const errorMessage = error.error || error.message || "Login failed";
        if (error.message === "Invalid password") {
          swalError(
            "The password you entered is incorrect. Please try again or use the 'Forgot Password' link to reset your password.",
          );
        } else {
          swalError(errorMessage, { timer: 5000, timerProgressBar: true });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelection = async (student) => {
    setIsAccountSelectionModalOpen(false);
    setLoading(true);

    try {
      const payload = {
        password: formData.password, // Use the password already entered
        studentId: student.studentId,
      };

      if (loginTab === "email") {
        payload.email = student.email;
      } else {
        payload.phoneNumber = cleanPhone(student.phoneNumber);
      }

      const response = await api.post("/auth/signin", payload);

      if (response.data.success) {
        await handleLoginSuccess();
      }
    } catch (error) {
      const errorMessage = error.error || error.message || "Login failed";

      if (error.message === "Invalid password") {
        swalError(
          "The password you entered is incorrect. Please try again or use the 'Forgot Password' link to reset your password.",
        );
        setIsAccountSelectionModalOpen(false);
        setMultipleAccounts(null);
      } else {
        swalError(errorMessage, { timer: 5000, timerProgressBar: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setLoading(true);

    try {
      const payload = {
        password: formData.password,
      };

      if (loginTab === "email") {
        payload.email = formData.email;
      } else {
        payload.phoneNumber = cleanPhone(formData.phoneNumber);
      }

      const response = await api.post("/auth/signin", payload);

      if (response.data.success) {
        setShowPaymentPrompt(false);
        setPaymentToken(null);

        await handleLoginSuccess();
      } else {
        swalError("Payment succeeded, but automatic login failed. Please log in again.");
      }
    } catch (error) {
      console.error("Auto login after payment failed:", error);

      const errorMessage =
        error.error || error.message || "Automatic login failed.";

      swalError(
        `Payment was successful, but automatic login failed: ${errorMessage}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  const handlePhoneChange = (value, data, event, formattedValue) => {
    let cleanValue = value || "";
    let currentDialCode = "";
    if (data && data.dialCode) {
      currentDialCode = data.dialCode;
      if (cleanValue.startsWith(data.dialCode)) {
        let nationalNumber = cleanValue.slice(data.dialCode.length);
        cleanValue = data.dialCode + nationalNumber.replace(/^0+/, "");
      } else {
        cleanValue = data.dialCode + cleanValue.replace(/^0+/, "");
      }
    }
    setPhoneDialCode(currentDialCode);
    const cleaned = cleanPhone(cleanValue);
    setFormData((prev) => ({
      ...prev,
      phoneNumber: cleaned,
    }));
  };

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleTabChange = (tab) => {
    setLoginTab(tab);
    setFormData({ email: "", password: "", phoneNumber: "" });
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    try {
      const payload = {};

      if (forgotPasswordTab === "email") {
        payload.email = forgotPasswordEmail;
      } else {
        const phoneValidation = validateStrictPhoneNumber(forgotPasswordPhone, forgotPasswordPhoneDialCode);
        if (!phoneValidation.isValid) {
          swalError(phoneValidation.message);
          setLoading(false);
          return;
        }
        payload.phoneNumber = cleanPhone(forgotPasswordPhone);
      }

      const response = await api.post("/user/forgot-password", payload);

      if (response.status === 200) {
        swalSuccess(
          `A password reset link has been sent to your ${
            forgotPasswordTab === "email" ? "email address" : "phone number"
          }.`,
        );
        setIsForgotPasswordModalOpen(false);
        setForgotPasswordEmail("");
        setForgotPasswordPhone("");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to send reset link.";
      swalError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordTabChange = (tab) => {
    setForgotPasswordTab(tab);
    setForgotPasswordEmail("");
    setForgotPasswordPhone("");
  };

  const handleForgotPasswordModalClose = () => {
    setIsForgotPasswordModalOpen(false);
    setForgotPasswordEmail("");
    setForgotPasswordPhone("");
    setForgotPasswordTab("email");
  };

  return (
    <motion.div
      className="login-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <img src="/imgs/logo.svg" alt="" width={400} />
      <motion.div
        className="login-box"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h2
          initial={{ x: -50 }}
          animate={{ x: 0 }}
          transition={{ delay: 0.3 }}
        >
          Welcome Back!
        </motion.h2>
        <hr className="login__hr" />

        {/* Google Sign In Button */}
        {/* <motion.div 
        className="google-signin-section"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <motion.button
          className="google-signin-btn"
          onClick={() => {
            window.location.href = `${serverUrl}/api/oauth/get-token?isGoogleSSOUser=true`;
          }}
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <img
            src="https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/dark/google-color.png"
            alt="Google"
            className="google-icon"
          />
          <span>Continue with Google</span>
        </motion.button>

        <div className="login-divider">
          <span>or</span>
        </div>
      </motion.div> */}

        {/* Login Tabs */}
        <div className="login-tabs">
          <motion.button
            className={`login-tab ${loginTab === "phone" ? "active" : ""}`}
            onClick={() => handleTabChange("phone")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <i className="fa-solid fa-phone"></i>
            Phone
          </motion.button>
          <motion.button
            className={`login-tab ${loginTab === "email" ? "active" : ""}`}
            onClick={() => handleTabChange("email")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <i className="fa-solid fa-envelope"></i>
            Email
          </motion.button>
        </div>

        <motion.h3
          initial={{ y: -50 }}
          animate={{ y: 0 }}
          transition={{ delay: 0.3 }}
        >
          Enter Your Credentials
        </motion.h3>
        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {loginTab === "phone" ? (
              <motion.div
                key="phone-fields"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="phone-input-wrapper"
              >
                <div className="input-group">
                  <label
                    htmlFor="phoneNumber"
                    style={{
                      ...((isPhoneFocused || formData.phoneNumber) ? {
                        fontSize: "0.8rem",
                        background: "linear-gradient(45deg, #636363, #222222)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      } : {})
                    }}
                  >
                    <i className="fa-solid fa-asterisk requiredIcon"></i> Phone
                    Number
                  </label>
                  <PhoneInput
                    placeholder="Enter Phone Number"
                    country="in"
                    value={formData.phoneNumber}
                    onFocus={() => setIsPhoneFocused(true)}
                    onBlur={() => setIsPhoneFocused(false)}
                    onChange={handlePhoneChange}
                    inputStyle={{
                      height: "50px",
                      fontSize: "16px",
                      width: "100%",
                      paddingLeft: "48px",
                      border: "2px solid rgba(0, 0, 0, 0.2)",
                      borderRadius: "50px",
                      background: "rgba(255, 255, 255, 0.1)",
                      color: "black",
                    }}
                    buttonStyle={{
                      height: "50px",
                      border: "2px solid rgba(0, 0, 0, 0.2)",
                      borderRadius: "50px 0 0 50px",
                      background: "rgba(255, 255, 255, 0.1)",
                    }}
                    containerStyle={{
                      width: "100%",
                    }}
                    enableSearch={true}
                    disableSearchIcon={true}
                    dropdownClass="phone-input-dropdown"
                    dropdownStyle={{
                      maxHeight: "300px",
                      overflowX: "hidden",
                    }}
                    inputProps={{
                      onFocus: () => setIsPhoneFocused(true),
                      onBlur: () => setIsPhoneFocused(false)
                    }}
                    disableCountryGuess={true}
                  />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="email-fields"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="input-group">
                  <input
                    type="text"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                  <label htmlFor="email">
                    <i className="fa-solid fa-asterisk requiredIcon"></i> Email
                  </label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="input-group" style={{ marginBottom: ".5rem" }}>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
            />
            <label htmlFor="password">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Password
            </label>
            <motion.span className="toggle-password" onClick={togglePassword}>
              <i
                className={`fa-regular ${
                  showPassword ? "fa-eye-slash" : "fa-eye"
                }`}
              />
            </motion.span>
          </div>
          <Link
            to="#"
            className="forgot-password-link"
            onClick={() => setIsForgotPasswordModalOpen(true)}
          >
            Forgot Password?
          </Link>


          <motion.div
            className="login-actions"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            {turnstileSiteKey && (
              <div
                className="turnstile-container"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "1.25rem",
                  minHeight: "65px",
                }}
              >
                <TurnstileWidget
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => setTurnstileToken("")}
                  onExpire={() => setTurnstileToken("")}
                  theme="light"
                  appearance="always"
                />
              </div>
            )}

            {!loading ? (
              <motion.button
                type="submit"
                className="login-button"
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
                }}
                whileTap={{ scale: 0.98 }}
              >
                Login
              </motion.button>
            ) : (
              <Loader />
            )}

            <motion.p className="signup-text">
              New to GradeMe-AI?{" "}
              <motion.a
                href="https://grademe-ai.com?contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                Contact Us
              </motion.a>{" "}
              to set up your account!{" "}
            </motion.p>
          </motion.div>
        </form>
      </motion.div>

      {/* Forgot Password Modal */}
      <Modal
        className="forgot-password-modal"
        isOpen={isForgotPasswordModalOpen}
        onClose={handleForgotPasswordModalClose}
        modalContentClass="forgot-password-modal-content"
      >
        <h2>Forgot Password?</h2>

        {/* Forgot Password Tabs */}
        <div className="login-tabs" style={{ marginBottom: "1rem" }}>
          <motion.button
            className={`login-tab ${
              forgotPasswordTab === "email" ? "active" : ""
            }`}
            onClick={() => handleForgotPasswordTabChange("email")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <i className="fa-solid fa-envelope"></i>
            Email
          </motion.button>
          <motion.button
            className={`login-tab ${
              forgotPasswordTab === "phone" ? "active" : ""
            }`}
            onClick={() => handleForgotPasswordTabChange("phone")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <i className="fa-solid fa-phone"></i>
            Phone
          </motion.button>
        </div>

        <p>
          Enter your{" "}
          {forgotPasswordTab === "email" ? "email address" : "phone number"} and
          we'll send you a link to reset your password{" "}
          {forgotPasswordTab === "email" ? "" : "on your WhatsApp"}.
        </p>

        <AnimatePresence mode="wait">
          {forgotPasswordTab === "email" ? (
            <motion.div
              key="forgot-email"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="form-group"
            >
              <label htmlFor="forgotPasswordEmail">
                <i className="fa-solid fa-asterisk requiredIcon"></i> Email
              </label>
              <input
                type="email"
                id="forgotPasswordEmail"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </motion.div>
          ) : (
            <motion.div
              key="forgot-phone"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="form-group"
            >
              <label htmlFor="forgotPasswordPhone">
                <i className="fa-solid fa-asterisk requiredIcon"></i> Phone
                Number
              </label>
              <PhoneInput
                placeholder="Enter Phone Number"
                country="in"
                value={forgotPasswordPhone}
                onChange={(value, data, event, formattedValue) => {
                  let cleanValue = value || "";
                  let currentDialCode = "";
                  if (data && data.dialCode) {
                    currentDialCode = data.dialCode;
                    if (cleanValue.startsWith(data.dialCode)) {
                      let nationalNumber = cleanValue.slice(
                        data.dialCode.length,
                      );
                      cleanValue =
                        data.dialCode + nationalNumber.replace(/^0+/, "");
                    } else {
                      cleanValue =
                        data.dialCode + cleanValue.replace(/^0+/, "");
                    }
                  }
                  setForgotPasswordPhoneDialCode(currentDialCode);
                  const cleaned = cleanPhone(cleanValue);
                  setForgotPasswordPhone(cleaned);
                }}
                inputStyle={{
                  height: "50px",
                  fontSize: "16px",
                  width: "100%",
                  paddingLeft: "48px",
                  border: "2px solid rgba(0, 0, 0, 0.2)",
                  borderRadius: "50px",
                  background: "rgba(255, 255, 255, 0.1)",
                  color: "black",
                }}
                buttonStyle={{
                  height: "50px",
                  border: "2px solid rgba(0, 0, 0, 0.2)",
                  borderRadius: "50px 0 0 50px",
                  background: "rgba(255, 255, 255, 0.1)",
                }}
                containerStyle={{
                  width: "100%",
                  marginTop: "0.5rem",
                }}
                enableSearch={true}
                disableSearchIcon={true}
                dropdownClass="phone-input-dropdown"
                dropdownStyle={{
                  maxHeight: "300px",
                  overflowX: "hidden",
                }}
                disableCountryGuess={true}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {!loading ? (
          <motion.button
            className="login-button"
            onClick={handleForgotPassword}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
            }}
            whileTap={{ scale: 0.98 }}
          >
            Send Reset Link
          </motion.button>
        ) : (
          <Loader />
        )}
      </Modal>

      {/* Account Selection Modal */}
      <Modal
        className="account-selection-modal"
        isOpen={isAccountSelectionModalOpen}
        onClose={() => {
          setIsAccountSelectionModalOpen(false);
          setMultipleAccounts(null);
        }}
        modalContentClass="account-selection-modal-content"
      >
        <h2>Select Account</h2>
        <p>
          Multiple accounts found with this{" "}
          {loginTab === "email" ? "email" : "phone number"}. Please select the
          account you want to login with.
        </p>
        <div className="account-list">
          {multipleAccounts?.map((student) => (
            <motion.div
              key={student.studentId}
              className="account-item"
              onClick={() => handleAccountSelection(student)}
              whileHover={{ scale: 1.02, backgroundColor: "#f5f5f5" }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="account-avatar">
                <img src="/imgs/person_icon.svg" alt="user_img" width={50} />
              </div>
              <div className="account-details">
                <h3>
                  {student.firstName} {student.lastName}
                </h3>
                {student.instituteName && (
                  <p
                    className="account-institute"
                    style={{
                      margin: "0.25rem 0 0 0",
                      fontSize: "0.85rem",
                      color: "#64748b",
                      fontWeight: 500,
                    }}
                  >
                    <i
                      className="fa-solid fa-graduation-cap"
                      style={{ marginRight: "0.35rem", color: "#8b5cf6" }}
                    />
                    {student.instituteName}
                  </p>
                )}
                <p className="account-email">{student.email}</p>
                {student.phoneNumber && (
                  <p className="account-phone">+{student.phoneNumber}</p>
                )}
              </div>
              <div className="account-arrow">
                <i className="fa-solid fa-chevron-right"></i>
              </div>
            </motion.div>
          ))}
        </div>
      </Modal>

      {/* Install Prompt Modal */}
      <Modal
        className="install-prompt-modal"
        isOpen={showInstallPrompt}
        onClose={() => setShowInstallPrompt(false)}
      >
        <div className="install-prompt-content">
          <div className="install-prompt-badge-container">
            <div className="app-icon-badge">
              <img
                src="/imgs/icons/android-chrome-192x192.png"
                alt="GradeMe-AI Icon"
              />
            </div>
            <h2>Install GradeMe AI</h2>
            <p className="install-subtext">
              Install the app for instant notifications, faster access, and a seamless mobile experience.
            </p>
          </div>

          <div className="features-list">
            <div className="feature-item-row">
              <div className="feature-icon-box">
                <i className="fa-solid fa-bell"></i>
              </div>
              <div className="feature-text-content">
                <strong>Instant Alerts</strong>
                <span>Get real-time exam and grading notifications</span>
              </div>
            </div>
            <div className="feature-item-row">
              <div className="feature-icon-box">
                <i className="fa-solid fa-bolt"></i>
              </div>
              <div className="feature-text-content">
                <strong>1-Tap Launch</strong>
                <span>Instant access directly from your home screen</span>
              </div>
            </div>
            <div className="feature-item-row">
              <div className="feature-icon-box">
                <i className="fa-solid fa-mobile-screen-button"></i>
              </div>
              <div className="feature-text-content">
                <strong>Full Screen Experience</strong>
                <span>Native app speed with zero browser clutter</span>
              </div>
            </div>
          </div>

          {(showManualInstructions || !deferredPrompt) && (
            <div className="install-instructions">
              {isIOS ? (
                <p>
                  <i className="fa-solid fa-circle-info"></i> Tap the <strong>Share</strong> icon{" "}
                  <i className="fa-solid fa-arrow-up-from-bracket"></i> in Safari, then select{" "}
                  <strong>"Add to Home Screen"</strong>.
                </p>
              ) : (
                <p>
                  <i className="fa-solid fa-circle-info"></i> Tap the browser menu{" "}
                  <i className="fa-solid fa-ellipsis-vertical"></i> in Chrome, then select{" "}
                  <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.
                </p>
              )}
            </div>
          )}

          <div className="install-actions">
            <motion.button
              className="install-button"
              onClick={handleInstallClick}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <i className="fa-solid fa-download"></i>
              Install App
            </motion.button>
            <motion.button
              className="maybe-later-button"
              onClick={() => setShowInstallPrompt(false)}
              whileHover={{ scale: 1.02 }}
            >
              Maybe Later
            </motion.button>
          </div>
        </div>
      </Modal>
      
      
      <PaymentModal
        isOpen={showPaymentPrompt}
        onClose={() => {
          setShowPaymentPrompt(false);
          setPaymentToken(null);
        }}
        paymentToken={paymentToken}
        onSuccess={handlePaymentSuccess}
      />

      
    </motion.div>
  );
}
